import { Header, Table } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { getPerformerAndVenueForEvent } from 'src/utils/eventWithDataUtils';
import {
  AutoPricingResultType,
  FilterOperationType,
} from 'src/WebApiController';

import {
  BooleanFilterUI,
  DatePresetFilterUI,
  DateRangeFilterUI,
  DualModeNumberFilterUI,
  extractUniqueValues,
  MoneyRangeFilterUI,
  MultiSelectFilterUI,
  NumberRangeFilterUI,
  TextSearchFilterUI,
  UserTagFilterUI,
} from '../filters/FilterComponents';
import { DatePresetValue } from '../filters/filterFunctions';
import {
  percentRangeFilter,
  seatRangeFilter,
} from '../filters/filterFunctions';

interface ColumnHeaderFilterProps<T extends Record<string, any>> {
  table: Table<T>;
  header: Header<T, unknown>;
}

export const ColumnHeaderFilter = <T extends Record<string, any>>({
  table,
  header,
}: ColumnHeaderFilterProps<T>) => {
  // Get catalog data for venue lookup (needed for marketplace broadcast)
  const { data: catalogData } = useCatalogDataContext();

  const filterValue = header.column.getFilterValue();

  const onChange = useCallback(
    (newValue: any) => {
      table.resetRowSelection();
      header.column.setFilterValue(newValue);
    },
    [table, header.column]
  );

  const onClear = useCallback(() => {
    table.resetRowSelection();
    header.column.setFilterValue(undefined);
  }, [table, header.column]);

  if (!header.column.getCanFilter()) {
    return null;
  }

  const columnFilterVariant = header.column.columnDef.meta?.columnFilterVariant;
  if (!columnFilterVariant) {
    return null;
  }

  // Simple switch based on columnFilterVariant
  switch (columnFilterVariant) {
    case 'numberRange': {
      const isPercent = header.column.columnDef.filterFn === percentRangeFilter;
      const isSeatRange = header.column.columnDef.filterFn === seatRangeFilter;
      // Don't extract options for percent or seat range columns

      const shouldExtractOptions = !isPercent && !isSeatRange;

      const { options, counts } = shouldExtractOptions
        ? extractUniqueValues(
            table.getCoreRowModel().flatRows,
            header.column.id,
            undefined,
            undefined
          )
        : { options: {}, counts: {} };

      return (
        <NumberRangeFilterUI
          value={
            (filterValue as {
              min?: number;
              max?: number;
              selectedValues: string[];
            }) ?? { min: undefined, max: undefined, selectedValues: [] }
          }
          options={options}
          counts={counts}
          onChange={onChange}
          onClear={onClear}
          columnId={header.column.id}
          table={table}
          isPercent={isPercent}
        />
      );
    }

    case 'multiSelect': {
      const uniqueValueVariant =
        header.column.columnDef.meta?.uniqueValueVariant;

      // Get venue for marketplace broadcast variant
      let venue = null;
      if (uniqueValueVariant === 'marketplaceBroadcast') {
        const firstRow = table
          .getCoreRowModel()
          .flatRows.find((r) => r.original?.listing);
        const event = firstRow?.original?.event;
        if (event && catalogData) {
          const fullInfo = getPerformerAndVenueForEvent(event, catalogData);
          venue = fullInfo.venue;
        }
      }

      const { options, counts } = extractUniqueValues(
        table.getCoreRowModel().flatRows,
        header.column.id,
        uniqueValueVariant,
        venue
      );

      return (
        <MultiSelectFilterUI
          value={(filterValue as string[]) ?? []}
          onChange={onChange}
          onClear={onClear}
          options={options}
          counts={counts}
          columnId={header.column.id}
          table={table}
          uniqueValueVariant={uniqueValueVariant}
        />
      );
    }

    case 'textSearch': {
      return (
        <TextSearchFilterUI
          value={(filterValue as string) ?? ''}
          onChange={onChange}
          onClear={onClear}
          options={{}}
          columnId={header.column.id}
          table={table}
        />
      );
    }

    case 'dateRange': {
      const dateRangeValue = filterValue as
        | { startDate?: Date; endDate?: Date; operation?: FilterOperationType }
        | [Date, Date]
        | undefined;

      // Handle legacy array format [startDate, endDate]
      const normalizedValue = Array.isArray(dateRangeValue)
        ? {
            startDate: dateRangeValue[0],
            endDate: dateRangeValue[1],
            operation: FilterOperationType.Between,
          }
        : (dateRangeValue ?? {
            startDate: undefined,
            endDate: undefined,
            operation: FilterOperationType.Between,
          });

      return (
        <DateRangeFilterUI
          value={normalizedValue}
          onChange={onChange}
          onClear={onClear}
          options={{}}
          columnId={header.column.id}
          table={table}
        />
      );
    }

    case 'moneyRange': {
      return (
        <MoneyRangeFilterUI
          value={
            (filterValue as {
              currency?: string;
              min?: number;
              max?: number;
              operation?: FilterOperationType;
              autoPricingResultType?: AutoPricingResultType;
            }) ?? {
              currency: undefined,
              min: undefined,
              max: undefined,
              operation: FilterOperationType.Between,
              autoPricingResultType: undefined,
            }
          }
          onChange={onChange}
          onClear={onClear}
          options={{}}
          columnId={header.column.id}
          table={table}
        />
      );
    }

    case 'boolean': {
      return (
        <BooleanFilterUI
          value={(filterValue as boolean | null) ?? null}
          onChange={onChange}
          onClear={onClear}
          options={{}}
          columnId={header.column.id}
          table={table}
        />
      );
    }

    case 'dualModeNumber': {
      return (
        <DualModeNumberFilterUI
          value={
            (filterValue as {
              mode?: 'absolute' | 'relative';
              min?: number;
              max?: number;
            }) ?? {
              mode: undefined,
              min: undefined,
              max: undefined,
            }
          }
          onChange={onChange}
          onClear={onClear}
          options={{}}
          columnId={header.column.id}
          table={table}
        />
      );
    }

    case 'userTag': {
      return (
        <UserTagFilterUI
          value={(filterValue as string[]) ?? []}
          onChange={onChange}
          onClear={onClear}
          columnId={header.column.id}
          table={table}
        />
      );
    }

    case 'datePreset': {
      return (
        <DatePresetFilterUI
          value={(filterValue as DatePresetValue) ?? undefined}
          onChange={onChange}
          onClear={onClear}
          columnId={header.column.id}
          table={table}
        />
      );
    }

    default:
      return null;
  }
};
